/**
 * Colours the changes the agent writes into its prose.
 *
 * The narrative reports movement as `▲ +1.3 pts` / `▼ -1.0 pts`. The arrow
 * and the number are wrapped in a coloured span — green up, red down — and
 * nothing else changes: the text inside the span is exactly what the agent
 * wrote, sign and unit included, and whatever follows (`, z = 2.4`) stays as
 * written. The panel styles the agent's words; it does not add to them.
 *
 * `splitDeltas` does the reading; the rehype plugin applies it to a reply's
 * parsed tree (so text inside code is never touched), and the cards apply it
 * to their own metric lines so both read the same way.
 */

const NUM = String.raw`[+\-−]?\d+(?:\.\d+)?`;

// Arrow · number · optional unit — matched as written, never rewritten.
const DELTA = new RegExp(String.raw`([▲▼])\s*${NUM}(?:\s*(?:pts?|pp|points?|%))?`, "g");

/**
 * Text in, a list of plain runs and changes out. Returns null when the text
 * holds no change, so callers can keep the original untouched.
 */
export const splitDeltas = (value) => {
  if (typeof value !== "string" || !/[▲▼]/.test(value)) return null;
  const parts = [];
  let last = 0;
  for (const m of value.matchAll(DELTA)) {
    if (m.index > last) parts.push({ type: "text", value: value.slice(last, m.index) });
    parts.push({ type: "delta", direction: m[1] === "▲" ? "up" : "down", text: m[0] });
    last = m.index + m[0].length;
  }
  if (!parts.length) return null;
  if (last < value.length) parts.push({ type: "text", value: value.slice(last) });
  return parts;
};

const SKIP = new Set(["code", "pre"]);

const toHast = (part) =>
  part.type === "text"
    ? part
    : {
        type: "element",
        tagName: "span",
        properties: { className: ["lou-delta", `lou-delta--${part.direction}`] },
        children: [{ type: "text", value: part.text }],
      };

const walk = (node) => {
  if (!node.children || SKIP.has(node.tagName)) return;
  node.children = node.children.flatMap((child) => {
    if (child.type === "text") return splitDeltas(child.value)?.map(toHast) ?? [child];
    walk(child);
    return [child];
  });
};

export const rehypeHighlightDeltas = () => (tree) => {
  walk(tree);
};

export default rehypeHighlightDeltas;
